import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, forkJoin } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthService } from '../../../../../pages/auth-pages/services/auth.service';
import { environment } from '../../../../../../environments/environment';
import { EMPTY_SEARCH_RESULTS, SearchEntityType, SearchResult, SearchResults } from '../models/global-search.model';
import { MenuVisibilityService } from '../../../../services/menu-visibility.service';


// Tickets: el endpoint que se usaba (`/api/support-tickets`) no existe en el
// backend — de ahí el error que motivó apagar esta sección. El real es
// `GET /api/tickets/search?q=` (busca por ticket_number exacto o subject ILIKE).
const TICKETS_SEARCH_ENABLED = true;

@Injectable({
  providedIn: 'root'
})
export class GlobalSearchService {
  private http = inject(HttpClient);
  private auth = inject(AuthService);
  private menuVisibility = inject(MenuVisibilityService);

  private readonly apiUrl = `${environment.apiUrl}api`;
  private readonly RESULTS_PER_ENTITY = 5;  // máximo 5 por sección — RF-16

  /**
   * Búsqueda en paralelo en las 5 entidades.
   * Si una falla, devuelve [] para esa entidad — no rompe el dropdown.
   */
  search(term: string): Observable<SearchResults> {
    const cleanTerm = term?.trim();
    if (!cleanTerm || cleanTerm.length < 2) {
        return of(EMPTY_SEARCH_RESULTS);
    }

    const businessId = this.auth.getBusinessId() ?? 1;

    // Navegación rápida (síncrona, no necesita HTTP)
    const navigation = this.searchNavigation(cleanTerm);

    return forkJoin({
        contacts: this.searchContacts(businessId, cleanTerm),
        companies: this.searchCompanies(businessId, cleanTerm),
        deals: this.searchDeals(businessId, cleanTerm),
        tasks: this.searchTasks(cleanTerm),
        tickets: TICKETS_SEARCH_ENABLED ? this.searchTickets(cleanTerm) : of([])
    }).pipe(
        map(r => ({
        navigation,
        ...r,
        totalCount: navigation.length + r.contacts.length + r.companies.length
                    + r.deals.length + r.tasks.length + r.tickets.length,
        isLoading: false
        } as SearchResults))
    );
    }

  // ============================================================
  // Búsquedas por entidad — cada una mapea a SearchResult
  // ============================================================

  private searchContacts(businessId: number, term: string): Observable<SearchResult[]> {
    const params = new HttpParams()
      .set('businessId', businessId.toString())
      .set('searchTerm', term)
      .set('page', '0')
      .set('size', this.RESULTS_PER_ENTITY.toString());

    return this.http.get<any>(`${this.apiUrl}/contacts`, { params }).pipe(
      map(res => (res.content ?? []).map((c: any): SearchResult => ({
        type: 'contact',
        id: c.id,
        title: `${c.fullname ?? ''} ${c.lastname ?? ''}`.trim(),
        subtitle: c.mail ?? c.phone ?? undefined,
        // El detalle de un contacto es un MODAL sobre el kanban, no una ruta
        // propia: se abre con ?openContact=<id> (mismo patrón que ?openTicket=).
        // Buscar NO debe llevar al formulario de edición.
        routerLink: `/contactos?openContact=${c.id}`
      }))),
      catchError(() => of([]))
    );
  }

  private searchCompanies(businessId: number, term: string): Observable<SearchResult[]> {
    // El endpoint de companies probablemente acepta searchTerm o similar.
    // Si no acepta filtro, traemos las primeras y filtramos en cliente.
    const params = new HttpParams()
      .set('businessId', businessId.toString())
      .set('searchTerm', term)
      .set('page', '0')
      .set('size', this.RESULTS_PER_ENTITY.toString());

    return this.http.get<any>(`${this.apiUrl}/companies`, { params }).pipe(
      map(res => {
        const items = res.content ?? (Array.isArray(res) ? res : []);
        return items.slice(0, this.RESULTS_PER_ENTITY).map((c: any): SearchResult => ({
          type: 'company',
          id: c.id,
          title: c.commercialName ?? c.businessName ?? `Empresa ${c.id}`,
          subtitle: c.businessName !== c.commercialName ? c.businessName : c.phone ?? undefined,
          // Detalle = modal sobre el kanban de empresas → ?openCompany=<id>.
          routerLink: `/empresas?openCompany=${c.id}`
        }));
      }),
      catchError(() => of([]))
    );
  }

  private searchDeals(businessId: number, term: string): Observable<SearchResult[]> {
    const params = new HttpParams()
      .set('businessId', businessId.toString())
      .set('searchName', term)
      .set('page', '0')
      .set('size', this.RESULTS_PER_ENTITY.toString());

    return this.http.get<any>(`${this.apiUrl}/deals`, { params }).pipe(
      map(res => (res.content ?? []).map((d: any): SearchResult => ({
        type: 'deal',
        id: d.id,
        title: d.name ?? `Negocio ${d.id}`,
        subtitle: d.amount ? `S/ ${d.amount.toLocaleString('es-PE')}` : d.description ?? undefined,
        // Detalle = modal sobre el kanban de negocios → ?openDeal=<id>.
        routerLink: `/deals?openDeal=${d.id}`
      }))),
      catchError(() => of([]))
    );
  }

  private searchTasks(term: string): Observable<SearchResult[]> {
    const body = {
      search: term,
      page: 0,
      size: this.RESULTS_PER_ENTITY
    };
    return this.http.post<any>(`${this.apiUrl}/tasks/filter`, body).pipe(
      map(res => (res.content ?? []).map((t: any): SearchResult => ({
        type: 'task',
        id: t.id,
        title: t.title ?? `Tarea ${t.id}`,
        subtitle: t.datetimeDue
          ? new Date(t.datetimeDue).toLocaleDateString('es-PE')
          : t.description ?? undefined,
        // Detalle = modal sobre la lista de tareas → ?openTask=<id>.
        routerLink: `/tareas?openTask=${t.id}`
      }))),
      catchError(() => of([]))
    );
  }

  /**
   * GET /api/tickets/search?q= — devuelve un array plano (Flux, máx 100), no una
   * página; el tenant sale del token, no lleva businessId. Se recorta a los
   * primeros RESULTS_PER_ENTITY como el resto de secciones.
   */
  private searchTickets(term: string): Observable<SearchResult[]> {
    const params = new HttpParams().set('q', term);

    return this.http.get<any>(`${this.apiUrl}/tickets/search`, { params }).pipe(
      map(res => {
        const items = Array.isArray(res) ? res : (res?.content ?? []);
        return items.slice(0, this.RESULTS_PER_ENTITY).map((t: any): SearchResult => ({
          type: 'ticket',
          id: t.id,
          title: t.subject ?? `Ticket #${t.id}`,
          subtitle: t.ticketNumber ?? undefined,
          // Detalle = modal sobre la lista de tickets → ?openTicket=<id>.
          // (`/tickets/${id}` ni siquiera era una ruta registrada.)
          routerLink: `/tickets?openTicket=${t.id}`
        }));
      }),
      catchError(() => of([]))
    );
  }

  // Navegación rápida — accesos directos a módulos del CRM

    private searchNavigation(term: string): SearchResult[] {
      const cleanTerm = term.toLowerCase().trim();
      if (cleanTerm.length < 2) return [];

      const results: SearchResult[] = [];

      // Mismas reglas de visibilidad que el sidebar (INC_CRM_024 inc02/inc03):
      // si el rol no ve el módulo en el menú, tampoco debe poder llegar a él
      // escribiendo su nombre aquí. `advancedMode = true` para no cambiar el
      // comportamiento previo del buscador con las herramientas técnicas.
      for (const section of this.menuVisibility.sections(true)) {
        for (const item of section.items) {
          // Skip de items deshabilitados — su ruta no existe, no debe sugerirse.
          if (item.disabled) continue;

          const itemMatches = item.name.toLowerCase().includes(cleanTerm);

          // Item principal con path propio
          if (item.path && itemMatches) {
            results.push({
              type: 'navigation',
              id: 0,
              title: item.name,
              subtitle: `${section.title}`,
              routerLink: item.path,
              iconKey: 'navigation'
            });
          }

          // SubItems
          if (item.subItems) {
            for (const sub of item.subItems) {
              if (sub.disabled) continue;
              if (sub.name.toLowerCase().includes(cleanTerm) || itemMatches) {
                results.push({
                  type: 'navigation',
                  id: 0,
                  title: sub.name,
                  subtitle: `${section.title} → ${item.name}`,
                  routerLink: sub.path,
                  iconKey: 'navigation'
                });
              }
            }
          }
        }
      }

      return results.slice(0, 8);
    }   
}